import * as React from 'react';

import { Res, TicketLocationDTO, SoldProductTranslationPolicy } from 'core/api';
import { SessionProvider } from 'core/infrastructure';
import { BackActionButton, Content, Header, PhoneOnly, PosScreen, Footer } from 'pos/ui';
import * as Images from 'pos/Images';
import { TicketSelection } from 'pos/modules/helpers/ticket-selection';
import { Modal } from 'core/ui-next/modal';
import { HStack, VStack, Text, Box } from '@chakra-ui/react';
import { BasicButton, SvgIcon } from 'core/ui-next';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import z from 'zod';

type Props = {
	sourceLocation: TicketLocationDTO,
	targetLocation: TicketLocationDTO,
	ticketSelection: TicketSelection
} & Modal.Actions<{ keepPrices: boolean }>;

const stateSchema = z.object({
	keepPrices: z.boolean()
});

type State = z.infer<typeof stateSchema>;

function ConfirmScreen(props: Props) {

	const { ticketSelection, sourceLocation, targetLocation, cancel, close } = props;

	const [keepPrices] = React.useState<State>({
		keepPrices: SessionProvider.getCurrent().SoldProductTranslationPolicy === SoldProductTranslationPolicy.KeepOriginalPrice
	});

	const askPricePolicy = SessionProvider.getCurrent().SoldProductTranslationPolicy === SoldProductTranslationPolicy.AskUser;

	const { Form, RadioGroup } = useControlsFor<State>();
	const formRef = React.useRef<FormRef<State>>(null);

	const optionsItem = [
		{ value: true, label: Res.Web.CONFIRM_MOVE_TICKET_KEEP_PRICE_LBL() },
		{ value: false, label: Res.Web.CONFIRM_MOVE_TICKET_UPDATE_PRICE_LBL() }
	];

	const onAccept = React.useCallback(async () => {

		if (!formRef.current)
			return close({ keepPrices: keepPrices.keepPrices });

		const result = await formRef.current!.getValue();

		if (!result.ok)
			return;

		close(result.value);

	}, [close, keepPrices]);

	return <PosScreen screenId='CONFIRM_TICKET_MOVEMENT' size='small'>
		<Header title={Res.Web.CONFIRM_MOVE_TICKET()} image={Images.MoveTicket} onClose={cancel} />
		<Content>
			<VStack w='full' paddingY='14' paddingX='6' gap='6'>
				<VStack>
					<Text fontSize='md' fontWeight='semibold'>{Res.Web.CONFIRM_MOVE_TICKET_TITLE()}</Text>
					<Text fontSize='sm' fontWeight='regular'>{Res.Web.CONFIRM_MOVE_TICKET_TARGET_TITLE()}</Text>
					{ticketSelection.select === 'existing' &&
						<Text fontSize='sm' fontWeight='regular' color='warning'>{Res.Web.CONFIRM_MOVE_TICKET_NON_EMPTY_LOCATION_LBL(targetLocation.SaleCenterName + ' / ' + targetLocation.SaleLocationName)}</Text>
					}
				</VStack>
				<HStack w='full' gap='2'>
					<Box padding='4' width='36.5' height='19' bg={{ base: 'neutral.50', _dark: 'neutral.800' }} borderRadius={'md'} color={{ base: 'neutral.700', _dark: 'white' }}>
						<Text fontSize='sm' fontWeight='semibold'>{Res.Web.CONFIRM_MOVE_TICKET_SOURCE_LBL()}</Text>
						<Text fontSize='sm' fontWeight='regular' lineClamp={'2'}>{sourceLocation.SaleCenterName} / {sourceLocation.SaleLocationName}</Text>
					</Box>
					<Box borderRadius={'full'} width='5' height='5' color='info' bg={{ base: 'blue.50', _dark: 'blue.800' }} display='flex' alignItems='center' justifyContent='center'>
						<SvgIcon src={Images.IconAvance} />
					</Box>
					<Box padding='4' width='36.5' height='19' bg={{ base: 'blue.100', _dark: 'blue.700' }} borderRadius={'md'} color={{ base: 'neutral.700', _dark: 'white' }}>
						<Text fontSize='sm' fontWeight='semibold'>{Res.Web.CONFIRM_MOVE_TICKET_TARGET_LBL()}</Text>
						<Text fontSize='sm' fontWeight='regular' lineClamp={'2'}>{targetLocation.SaleCenterName} / {targetLocation.SaleLocationName}</Text>
					</Box>
				</HStack>
				{askPricePolicy &&
					<VStack>
						<Text>{Res.Web.CONFIRM_MOVE_TICKET_PRICE_POLICY_LBL()}</Text>
						<Form ref={formRef} initialValue={keepPrices} schema={stateSchema}>
							<VStack>
								<RadioGroup linkTo='keepPrices' options={optionsItem} size={'sm'} />
							</VStack>
						</Form>
					</VStack>
				}
				<BasicButton type='success' text={Res.Web.CONFIRM_MOVE_TICKET()} onClick={onAccept} />
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={async () => cancel()} />
			</PhoneOnly>
		</Footer>
	</PosScreen>;
}

export async function confirmMovement(sourceLocation: TicketLocationDTO, targetLocation: TicketLocationDTO, ticketSelection: TicketSelection): Promise<Result<{ keepPrices: boolean }>> {
	return Modal.show(ConfirmScreen, { sourceLocation, targetLocation, ticketSelection });
}

